import { Star, X } from "lucide-react";
import { useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useCompanyTenets } from "@/hooks/use-company-tenets";
import {
  useAddEndorsement,
  useMyEndorsements,
  useRemoveEndorsement,
} from "@/hooks/use-review-endorsements";

/**
 * Endorsing a question on the review you are filling in — EPIC-062.
 *
 * The count is shown at all times and the form disappears at the cap, because
 * the scarcity IS the feature: an endorsement that costs nothing says nothing.
 * The number is set per round, so it is not something a reviewer can be
 * assumed to know — hence "2 of 3 left" rather than a bare "add one".
 *
 * A detail is required, and the Save button stays disabled without one. That
 * is the whole mechanism: singling somebody out obliges you to say why.
 */
export function ReviewEndorsements({
  reviewerId,
  locked,
}: {
  reviewerId: string;
  locked: boolean;
}) {
  const mine = useMyEndorsements(reviewerId);
  /*
    The company's tenets, not this review's questions. Endorsing is a statement
    about what somebody embodies, so the list is the same for everybody —
    which is also why it is fetched here rather than passed down from the form.
  */
  const tenets = useCompanyTenets(true);
  const add = useAddEndorsement(reviewerId);
  const remove = useRemoveEndorsement(reviewerId);
  const [tenetId, setTenetId] = useState("");
  const [detail, setDetail] = useState("");

  const data = mine.data;
  /**
   * A FAILED read is not "this round offers no endorsements".
   *
   * Both were one early return, so a dropped request showed the reviewer no
   * endorsement section and no error — and they handed in without using any of
   * their allocation, which cannot be undone afterwards.
   */
  if (mine.isError) {
    return (
      <DetailSection title="Endorsements">
        <ErrorState
          message="Your endorsements couldn't be loaded. Don't hand in until this works — they can't be added afterwards."
          onRetry={() => mine.refetch()}
        />
      </DetailSection>
    );
  }
  // A round can set the cap to zero, which turns endorsements off entirely.
  if (!data || data.cap === 0) return null;

  const left = Math.max(0, data.cap - data.used);
  const taken = new Set(data.rows.map((r) => r.tenetId));
  const available = (tenets.data ?? []).filter((t) => !taken.has(t.id));
  const ready = tenetId.length > 0 && detail.trim().length > 0;

  return (
    <DetailSection
      title="Endorsements"
      headerActions={
        <StatusPill
          tone={left === 0 ? "neutral" : "info"}
          label={left === 0 ? "All used" : `${left} of ${data.cap} left`}
        />
      }
    >
      <p className="mb-4 max-w-3xl text-muted-foreground text-sm">
        Name one of our tenets this person genuinely embodies, and say what they actually did. You
        only get {data.cap}, so they carry weight — and you can take one back if you change your
        mind.
      </p>

      {data.rows.length > 0 && (
        <ul className="mb-4 space-y-2">
          {data.rows.map((row) => (
            <li key={row.id} className="rounded-lg border border-border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className="flex items-center gap-2 font-medium text-sm">
                  <Star className="size-4 text-muted-foreground" aria-hidden />
                  {row.title}
                </span>
                {!locked && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove your endorsement of ${row.title}`}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(row.id)}
                  >
                    <X className="size-4" aria-hidden />
                  </Button>
                )}
              </div>
              <p className="mt-1 whitespace-pre-line text-muted-foreground text-sm">{row.detail}</p>
            </li>
          ))}
        </ul>
      )}

      {locked ? null : left === 0 ? (
        <p className="text-muted-foreground text-sm">
          You've used all {data.cap}. Remove one to free it up.
        </p>
      ) : tenets.isLoading ? null : (tenets.data ?? []).length === 0 ? (
        /*
          No tenets have been brought in yet. Said plainly rather than hidden:
          a reviewer who sees nothing here assumes the round has endorsements
          turned off, and hands in without asking.
        */
        <p className="text-muted-foreground text-sm">
          No company tenets have been set up yet, so there is nothing to endorse. Ask an
          administrator to bring them in under Settings.
        </p>
      ) : available.length === 0 ? (
        <p className="text-muted-foreground text-sm">You've endorsed every tenet on this review.</p>
      ) : (
        <div className="space-y-2 rounded-lg border border-border border-dashed p-3">
          <Select value={tenetId} onValueChange={setTenetId}>
            <SelectTrigger className="w-full sm:w-96" aria-label="Which tenet they embody">
              <SelectValue placeholder="Which tenet do they embody?" />
            </SelectTrigger>
            <SelectContent>
              {available.map((tenet) => (
                <SelectItem key={tenet.id} value={tenet.id}>
                  {tenet.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            value={detail}
            onChange={(event) => setDetail(event.target.value)}
            placeholder="What did they actually do, and what difference did it make?"
            rows={3}
          />
          <Button
            size="sm"
            disabled={!ready || add.isPending}
            onClick={() =>
              add.mutate(
                { tenetId, detail: detail.trim() },
                {
                  onSuccess: () => {
                    setTenetId("");
                    setDetail("");
                  },
                },
              )
            }
          >
            {add.isPending ? "Saving…" : "Endorse this"}
          </Button>
        </div>
      )}
    </DetailSection>
  );
}
